import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Shield,
  Plus,
  Search,
  Filter,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Calendar,
  User,
  MapPin
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || 'http://localhost:8001';

const RegistreControles = () => {
  const [controles, setControles] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState({});
  const [agents, setAgents] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterConforme, setFilterConforme] = useState('all');

  const [formData, setFormData] = useState({
    typeControle: 'agent',
    categorieControle: '',
    dateControle: new Date().toISOString().split('T')[0],
    heureControle: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    lieuControle: '',
    siteId: '',
    controleurId: '',
    controleurNom: '',
    controleurQualite: 'Responsable',
    agentControleId: '',
    agentControleNom: '',
    elementControle: '',
    conforme: true,
    observations: '',
    anomaliesDetectees: [],
    actionsCorrectivesImmediates: '',
    actionsCorrectives: '',
    delaiCorrection: '',
    documentsVerifies: [],
    necessiteSuivi: false,
    dateProchainControle: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [controlesRes, statsRes, categoriesRes, agentsRes, sitesRes] = await Promise.all([
        axios.get(`${API_URL}/api/registre-controles`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }).catch(error => {
          console.warn('Erreur contrôles:', error.message);
          return { data: [] };
        }),
        axios.get(`${API_URL}/api/registre-controles/stats`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }).catch(error => {
          console.warn('Erreur stats:', error.message);
          return { data: getDefaultStats() };
        }),
        axios.get(`${API_URL}/api/registre-controles/categories`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }).catch(error => {
          console.warn('Erreur catégories:', error.message);
          return { data: {} };
        }),
        axios.get(`${API_URL}/api/agents`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }).catch(error => {
          console.warn('Erreur agents:', error.message);
          return { data: [] };
        }),
        axios.get(`${API_URL}/api/sites`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }).catch(error => {
          console.warn('Erreur sites:', error.message);
          return { data: [] };
        })
      ]);

      setControles(controlesRes.data);
      setStats(statsRes.data);
      setCategories(categoriesRes.data);
      setAgents(agentsRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultStats = () => ({
    total: 0,
    conformes: 0,
    non_conformes: 0,
    taux_conformite: 0,
    en_attente_suivi: 0,
    par_type: {}
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(`${API_URL}/api/registre-controles`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsAddDialogOpen(false);
      loadData();
      // Reset form
      setFormData({
        typeControle: 'agent',
        categorieControle: '',
        dateControle: new Date().toISOString().split('T')[0],
        heureControle: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
        lieuControle: '',
        siteId: '',
        controleurId: '',
        controleurNom: '',
        controleurQualite: 'Responsable',
        agentControleId: '',
        agentControleNom: '',
        elementControle: '',
        conforme: true,
        observations: '',
        anomaliesDetectees: [],
        actionsCorrectivesImmediates: '',
        actionsCorrectives: '',
        delaiCorrection: '',
        documentsVerifies: [],
        necessiteSuivi: false,
        dateProchainControle: ''
      });
    } catch (error) {
      console.error('Erreur création contrôle:', error);
      alert('Erreur lors de la création du contrôle');
    }
  };

  const filteredControles = controles.filter(controle => {
    const matchSearch = controle.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       controle.elementControle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       controle.agentControleNom?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === 'all' || controle.typeControle === filterType;
    const matchConforme = filterConforme === 'all' || 
                         (filterConforme === 'conforme' && controle.conforme) ||
                         (filterConforme === 'non_conforme' && !controle.conforme);
    return matchSearch && matchType && matchConforme;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du registre...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Shield className="w-8 h-8 mr-3 text-orange-600" />
            Registre des Contrôles Internes CNAPS
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            ⚠️ Obligation légale - Article R631-20 du Code de la sécurité intérieure
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouveau contrôle
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <FileText className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-sm text-gray-600">Contrôles effectués</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
                <div className="text-2xl font-bold">{stats.conformes}</div>
                <div className="text-sm text-gray-600">Conformes</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <XCircle className="w-8 h-8 mx-auto mb-2 text-red-600" />
                <div className="text-2xl font-bold">{stats.non_conformes}</div>
                <div className="text-sm text-gray-600">Non conformes</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                <div className="text-2xl font-bold">{stats.taux_conformite}%</div>
                <div className="text-sm text-gray-600">Taux de conformité</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-yellow-600" />
                <div className="text-2xl font-bold">{stats.en_attente_suivi}</div>
                <div className="text-sm text-gray-600">En attente suivi</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Type de contrôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="agent">Agents</SelectItem>
                <SelectItem value="site">Sites</SelectItem>
                <SelectItem value="administratif">Administratif</SelectItem>
                <SelectItem value="materiel">Matériel</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterConforme} onValueChange={setFilterConforme}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Conformité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="conforme">Conformes</SelectItem>
                <SelectItem value="non_conforme">Non conformes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Liste des contrôles */}
      <Card>
        <CardHeader>
          <CardTitle>Historique des contrôles ({filteredControles.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredControles.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucun contrôle enregistré</p>
                <p className="text-sm mt-1">Commencez par créer votre premier contrôle interne</p>
              </div>
            ) : (
              filteredControles.map((controle) => (
                <div
                  key={controle.id}
                  className={`border rounded-lg p-4 ${
                    controle.conforme ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-semibold text-orange-600">{controle.numero}</span>
                        <span className="text-sm bg-gray-200 px-2 py-1 rounded">
                          {controle.typeControle}
                        </span>
                        {controle.conforme ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500 flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            Date
                          </div>
                          <div className="font-medium">
                            {new Date(controle.dateControle).toLocaleDateString('fr-FR')} - {controle.heureControle}
                          </div>
                        </div>

                        <div>
                          <div className="text-gray-500 flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            Lieu
                          </div>
                          <div className="font-medium">{controle.lieuControle}</div>
                        </div>

                        <div>
                          <div className="text-gray-500 flex items-center">
                            <User className="w-4 h-4 mr-1" />
                            Contrôleur
                          </div>
                          <div className="font-medium">{controle.controleurNom}</div>
                        </div>

                        <div>
                          <div className="text-gray-500">Élément contrôlé</div>
                          <div className="font-medium">{controle.elementControle}</div>
                        </div>
                      </div>

                      {!controle.conforme && controle.anomaliesDetectees?.length > 0 && (
                        <div className="mt-3 p-3 bg-red-100 rounded">
                          <div className="font-medium text-red-800 mb-1">Anomalies détectées:</div>
                          <ul className="text-sm text-red-700 list-disc list-inside">
                            {controle.anomaliesDetectees.map((anomalie, idx) => (
                              <li key={idx}>{anomalie}</li>
                            ))}
                          </ul>
                          {controle.actionsCorrectives && (
                            <div className="mt-2">
                              <div className="font-medium text-red-800">Actions correctives:</div>
                              <div className="text-sm text-red-700">{controle.actionsCorrectives}</div>
                            </div>
                          )}
                        </div>
                      )}

                      {controle.observations && (
                        <div className="mt-2 text-sm text-gray-700">
                          <span className="font-medium">Observations:</span> {controle.observations}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialog Ajout Contrôle */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouveau contrôle interne</DialogTitle>
            <DialogDescription>
              Enregistrement conforme aux exigences CNAPS
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Type de contrôle */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type de contrôle *</Label>
                <Select
                  value={formData.typeControle}
                  onValueChange={(value) => {
                    setFormData({ ...formData, typeControle: value, categorieControle: '' });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="site">Site</SelectItem>
                    <SelectItem value="administratif">Administratif</SelectItem>
                    <SelectItem value="materiel">Matériel</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Catégorie *</Label>
                <Select
                  value={formData.categorieControle}
                  onValueChange={(value) => setFormData({ ...formData, categorieControle: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories[formData.typeControle]?.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat.replace(/_/g, ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date et heure */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date du contrôle *</Label>
                <Input
                  type="date"
                  value={formData.dateControle}
                  onChange={(e) => setFormData({ ...formData, dateControle: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>Heure *</Label>
                <Input
                  type="time"
                  value={formData.heureControle}
                  onChange={(e) => setFormData({ ...formData, heureControle: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Lieu */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Lieu du contrôle *</Label>
                <Input
                  value={formData.lieuControle}
                  onChange={(e) => setFormData({ ...formData, lieuControle: e.target.value })}
                  placeholder="Ex: Bureau Paris, Site La Défense..."
                  required
                />
              </div>

              <div>
                <Label>Site (optionnel)</Label>
                <Select
                  value={formData.siteId}
                  onValueChange={(value) => setFormData({ ...formData, siteId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un site..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Contrôleur */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Nom du contrôleur *</Label>
                <Input
                  value={formData.controleurNom}
                  onChange={(e) => setFormData({ ...formData, controleurNom: e.target.value })}
                  placeholder="Prénom NOM"
                  required
                />
              </div>

              <div>
                <Label>Qualité *</Label>
                <Select
                  value={formData.controleurQualite}
                  onValueChange={(value) => setFormData({ ...formData, controleurQualite: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Directeur">Directeur</SelectItem>
                    <SelectItem value="Responsable">Responsable</SelectItem>
                    <SelectItem value="Chef d'équipe">Chef d'équipe</SelectItem>
                    <SelectItem value="Responsable QSE">Responsable QSE</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Agent contrôlé (si applicable)</Label>
                <Select
                  value={formData.agentControleId}
                  onValueChange={(value) => {
                    const agent = agents.find(a => a.id === value);
                    setFormData({
                      ...formData,
                      agentControleId: value,
                      agentControleNom: agent ? agent.name : ''
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Élément contrôlé */}
            <div>
              <Label>Élément contrôlé *</Label>
              <Input
                value={formData.elementControle}
                onChange={(e) => setFormData({ ...formData, elementControle: e.target.value })}
                placeholder="Ex: Carte professionnelle, Main courante, Moyens radio..."
                required
              />
            </div>

            {/* Conformité */}
            <div>
              <Label>Résultat du contrôle *</Label>
              <div className="flex items-center space-x-4 mt-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={formData.conforme}
                    onChange={() => setFormData({ ...formData, conforme: true })}
                    className="w-4 h-4 text-green-600"
                  />
                  <span className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-1 text-green-600" />
                    Conforme
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!formData.conforme}
                    onChange={() => setFormData({ ...formData, conforme: false })}
                    className="w-4 h-4 text-red-600"
                  />
                  <span className="flex items-center">
                    <XCircle className="w-5 h-5 mr-1 text-red-600" />
                    Non conforme
                  </span>
                </label>
              </div>
            </div>

            {/* Observations */}
            <div>
              <Label>Observations *</Label>
              <textarea
                value={formData.observations}
                onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                className="w-full border rounded-md p-2 min-h-[100px]"
                placeholder="Détails du contrôle, constatations..."
                required
              />
            </div>

            {/* Si non conforme */}
            {!formData.conforme && (
              <div className="space-y-4 p-4 bg-red-50 rounded-lg">
                <div>
                  <Label>Actions correctives *</Label>
                  <textarea
                    value={formData.actionsCorrectives}
                    onChange={(e) => setFormData({ ...formData, actionsCorrectives: e.target.value })}
                    className="w-full border rounded-md p-2 min-h-[80px]"
                    placeholder="Actions à mettre en œuvre pour corriger l'anomalie..."
                    required
                  />
                </div>

                <div>
                  <Label>Délai de correction</Label>
                  <Input
                    type="date"
                    value={formData.delaiCorrection}
                    onChange={(e) => setFormData({ ...formData, delaiCorrection: e.target.value })}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">
                Enregistrer le contrôle
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistreControles;
